import { useState, useEffect } from 'react'

interface Goal {
  id: number;
  name: string;
  target_amount: number;
  current_amount: number;
  inflation_rate: number;
  target_date: string | null;
}

interface GoalFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editGoal?: Goal | null;
}

export function GoalForm({
  isOpen,
  onClose,
  onSuccess,
  editGoal = null,
}: GoalFormProps) {
  const [name, setName] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [currentAmount, setCurrentAmount] = useState("");
  const [inflationRate, setInflationRate] = useState("3");
  const [targetDate, setTargetDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const isEditing = !!editGoal;

  useEffect(() => {
    if (editGoal) {
      setName(editGoal.name);
      setTargetAmount(editGoal.target_amount.toString());
      setCurrentAmount(editGoal.current_amount.toString());
      setInflationRate((editGoal.inflation_rate * 100).toString());
      setTargetDate(editGoal.target_date || "");
    } else {
      resetForm();
    }
  }, [editGoal, isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const url = isEditing ? `/api/goals/${editGoal.id}` : "/api/goals";
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          target_amount: parseFloat(targetAmount),
          current_amount: parseFloat(currentAmount) || 0,
          inflation_rate: parseFloat(inflationRate) / 100,
          target_date: targetDate || null,
        }),
      });

      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error || "Failed to save goal");
      }

      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }

  function resetForm() {
    setName("");
    setTargetAmount("");
    setCurrentAmount("");
    setInflationRate("3");
    setTargetDate("");
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-charcoal">
            {isEditing ? "Edit Goal" : "Create Goal"}
          </h2>
          <button
            onClick={onClose}
            className="text-stone hover:text-charcoal transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-danger/10 text-danger px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Goal Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
              placeholder="e.g., House Down Payment"
              required
            />
          </div>

          {/* Target Amount */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Target Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
                placeholder="60,000.00"
                required
              />
            </div>
          </div>

          {/* Current Amount */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Current Amount (saved so far)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={currentAmount}
                onChange={(e) => setCurrentAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Inflation Rate */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Inflation Rate (for adjustments)
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="0"
                max="20"
                value={inflationRate}
                onChange={(e) => setInflationRate(e.target.value)}
                className="w-full pr-8 pl-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
                placeholder="3"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone">
                %
              </span>
            </div>
            <p className="text-xs text-sage mt-1">
              Used to calculate inflation-adjusted target over time
            </p>
          </div>

          {/* Target Date */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Target Date (optional)
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-stone/30 rounded-lg text-charcoal hover:bg-cream transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2 px-4 bg-forest text-white rounded-lg hover:bg-forest-dark disabled:opacity-50 transition-colors"
            >
              {isLoading
                ? "Saving..."
                : isEditing
                ? "Update Goal"
                : "Create Goal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
